import { JSX, RefObject, useEffect, useRef, useState } from 'react';
import { usePublicConfigurationContext } from 'runtime-configuration';

import { cn } from '@videoblocks/design-system';

import { GoogleLoginSuccessCallback, useGoogleLoginIFrameEventListener } from './useGoogleLoginIFrameEventListener';

type Props = {
    successCallback: GoogleLoginSuccessCallback;
};

export function GoogleLoginOneTouchButton({ successCallback }: Props): JSX.Element {
    const ref = useRef<HTMLIFrameElement>(null);
    const { googleClientId } = usePublicConfigurationContext();
    useGoogleLoginIFrameEventListener(ref, googleClientId, successCallback);
    const isFocused = useIsIFrameFocused(ref);

    return (
        <div
            className={cn(
                'inline-flex rounded-xs',
                // Keep in sync with getAccessibleFocusStyles(); its focus-within variant never matches here.
                isFocused && 'outline-2 outline-offset-2 outline-white ring-2 ring-blue-600',
            )}
        >
            <iframe
                ref={ref}
                className="overflow-hidden w-[240px] h-10 border-0"
                sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                src={`/google-login-iframe.html?o=${encodeURIComponent(window.location.origin)}`}
                title="Sign in with Google"
            />
        </div>
    );
}

/**
 * Chrome matches neither `:focus` nor `:focus-within` when focus sits in Google's button, two iframes deep,
 * so focus is read from the parent window losing focus to this iframe instead.
 */
function useIsIFrameFocused(ref: RefObject<HTMLIFrameElement | null>): boolean {
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        const handleWindowBlur = () => setIsFocused(document.activeElement === ref.current);
        const handleWindowFocus = () => setIsFocused(false);
        window.addEventListener('blur', handleWindowBlur);
        window.addEventListener('focus', handleWindowFocus);
        return () => {
            window.removeEventListener('blur', handleWindowBlur);
            window.removeEventListener('focus', handleWindowFocus);
        };
    }, [ref]);

    return isFocused;
}
